/* global google */
import { useState, useEffect, useMemo } from 'react';
import { TextField, Autocomplete as MuiAutocomplete, Box, CircularProgress, Grid } from '@mui/material';
import { useJsApiLoader } from '@react-google-maps/api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import throttle from 'lodash/throttle';

const libraries: ('places')[] = ['places'];

interface PlaceOption {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlaceSelectedResult {
  address: string;
  coordinates: [number, number] | null;
  place_id: string;
}

interface AddressAutocompleteProps {
  label: string;
  value?: string;
  onInputChange: (value: string) => void;
  onPlaceSelected: (result: PlaceSelectedResult) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ label, value = '', onInputChange, onPlaceSelected, disabled = false, error = false, helperText = '' }) => {
  const [options, setOptions] = useState<PlaceOption[]>([]);
  const [isSelectingPlace, setIsSelectingPlace] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [isUserEditing, setIsUserEditing] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY,
    libraries,
    id: 'google-map-script-hkstylist'
  });

  // Throttled function to fetch place predictions using the new AutocompleteSuggestion API
  const fetchPredictions = useMemo(
    () =>
      throttle(async (input: string) => {
        if (!input || !isLoaded || !window.google?.maps?.places?.AutocompleteSuggestion) {
          setOptions([]);
          return;
        }

        try {
          // Create session token for billing optimization
          const sessionToken = new google.maps.places.AutocompleteSessionToken();

          // Use the new AutocompleteSuggestion.fetchAutocompleteSuggestions method
          const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: input,
            sessionToken: sessionToken,
            locationBias: {
              radius: 50000, // 50km radius
              center: { lat: 22.3193, lng: 114.1694 } // Hong Kong coordinates
            },
            includedRegionCodes: ['HK'], // Restrict to Hong Kong
            language: 'en'
          });

          // Transform suggestions to the format expected by the autocomplete
          interface PlaceSuggestion {
            placePrediction: {
              text: { text: string };
              placeId: string;
              structuredFormat?: {
                mainText?: { text: string };
                secondaryText?: { text: string };
              };
            };
          }
          const transformedOptions: PlaceOption[] = (suggestions as PlaceSuggestion[]).map((suggestion) => ({
            description: suggestion.placePrediction.text.text,
            place_id: suggestion.placePrediction.placeId,
            structured_formatting: {
              main_text: suggestion.placePrediction.structuredFormat?.mainText?.text || suggestion.placePrediction.text.text,
              secondary_text: suggestion.placePrediction.structuredFormat?.secondaryText?.text || ''
            }
          }));

          setOptions(transformedOptions);
        } catch (error) {
          console.error('Error fetching place predictions:', error);
          setOptions([]);
        }
      }, 300),
    [isLoaded]
  );

  useEffect(() => {
    if (value) {
      fetchPredictions(value);
    } else {
      setOptions([]);
    }
  }, [value, fetchPredictions]);

  // Effect to sync selectedAddress with external value
  useEffect(() => {
    // If external value exists and is different from selectedAddress, use external value
    // This handles both initialization and external updates
    if (value && value !== selectedAddress && !isUserEditing) {
      console.log(`[AddressAutocomplete] Syncing selectedAddress from external value: "${value}"`);
      setSelectedAddress(value);
      setIsUserEditing(false);
    }
    // Only clear selectedAddress if external value is empty AND we're not in the middle of selecting
    // AND the selectedAddress is not currently being edited by the user
    else if (!value && selectedAddress && !isSelectingPlace && !isUserEditing) {
      console.log(`[AddressAutocomplete] Clearing selectedAddress because external value is empty`);
      setSelectedAddress('');
    }
  }, [value, selectedAddress, isSelectingPlace, isUserEditing]);

  const currentInputValue = selectedAddress || value;

  console.log(`[AddressAutocomplete] Render - value: "${value}", selectedAddress: "${selectedAddress}", currentInputValue: "${currentInputValue}"`);

  const handlePlaceSelect = async (_event: React.SyntheticEvent, newValue: PlaceOption | null) => {
    if (!newValue || !isLoaded || !window.google?.maps?.places) return;

    console.log('[AddressAutocomplete] handlePlaceSelect called, setting isSelectingPlace to true');
    setIsSelectingPlace(true);

    try {
      // Import the places library to get the Place class
      const { Place } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;

      // Create a Place instance from the place ID
      const place = new Place({
        id: newValue.place_id,
        requestedLanguage: 'en'
      });

      // Fetch the required fields
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location']
      });

      // Extract coordinates from the place.location object
      let coordinates: [number, number] | null = null;

      if (place.location) {
        // In the new Places API, lat and lng are functions that need to be called
        const latVal = place.location.lat;
        const lngVal = place.location.lng;
        const lat: number = typeof latVal === 'function' ? (latVal as () => number)() : latVal as number;
        const lng: number = typeof lngVal === 'function' ? (lngVal as () => number)() : lngVal as number;

        if (lat !== undefined && lng !== undefined) {
          // MongoDB 2dsphere index expects coordinates in [longitude, latitude] format (GeoJSON standard)
          coordinates = [lng, lat];
        }
      }

      // Call the callback with the place details
      if (onPlaceSelected) {
        const addressToUse = place.formattedAddress || newValue.description;
        console.log('[AddressAutocomplete] Setting selectedAddress to:', addressToUse);
        setSelectedAddress(addressToUse);
        setIsUserEditing(false); // Reset editing flag when place is selected
        onPlaceSelected({
          address: addressToUse,
          coordinates: coordinates,
          place_id: newValue.place_id
        });
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      // Fallback to just the address
      if (onPlaceSelected) {
        const addressToUse = newValue.description;
        console.log('[AddressAutocomplete] Setting selectedAddress to (fallback):', addressToUse);
        setSelectedAddress(addressToUse);
        setIsUserEditing(false); // Reset editing flag when place is selected
        onPlaceSelected({
          address: addressToUse,
          coordinates: null,
          place_id: newValue.place_id
        });
      }
    } finally {
      // Reset the flag after a short delay to allow the autocomplete to settle
      setTimeout(() => {
        console.log('[AddressAutocomplete] Resetting isSelectingPlace to false');
        setIsSelectingPlace(false);
      }, 500);
    }
  };

  if (loadError) {
    console.error('Google Maps API failed to load:', loadError);
    return (
      <TextField
        label={label}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange && onInputChange(e.target.value)}
        disabled={disabled}
        placeholder="Google Maps failed to load"
        error
        helperText="Unable to load address autocomplete"
        fullWidth
      />
    );
  }

  if (!isLoaded) {
    return (
      <TextField
        label={label}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange && onInputChange(e.target.value)}
        disabled={disabled}
        placeholder="Loading..."
        InputProps={{
          endAdornment: <CircularProgress size={20} />
        }}
        fullWidth
      />
    );
  }

  return (
    <MuiAutocomplete
      options={options}
      getOptionLabel={(option) => option.description || ''}
      filterOptions={(x) => x} // Disable built-in filtering since we handle it with Google API
      value={null} // Always controlled externally
      onChange={handlePlaceSelect}
      inputValue={currentInputValue}
      onInputChange={(_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
        console.log(`[AddressAutocomplete] onInputChange called with: "${newInputValue}", reason: "${reason}", selectedAddress: "${selectedAddress}", isSelectingPlace: ${isSelectingPlace}`);

        // Don't call onInputChange if we're in the middle of selecting a place
        // and the new input value is empty (which happens after place selection)
        if (isSelectingPlace && newInputValue === '' && reason !== 'input') {
          console.log('[AddressAutocomplete] Blocking onInputChange - currently selecting place');
          return;
        }

                 // Only block clearing if it's not from user input (typing/backspace)
         // Allow user to clear/edit the field by typing or backspace
         if (reason === 'input' || reason === 'clear') {
           // User is actively typing or clearing - allow all changes
           console.log('[AddressAutocomplete] User input detected - allowing change');
           setIsUserEditing(true);

           // If user is typing something different from selectedAddress, clear it
           if (newInputValue !== selectedAddress) {
             console.log('[AddressAutocomplete] User is typing, clearing selectedAddress');
             setSelectedAddress('');
           }

           console.log(`[AddressAutocomplete] Calling onInputChange with: "${newInputValue}"`);
           if (onInputChange) {
             onInputChange(newInputValue);
           }
           return;
         }

        // For non-input reasons (like blur, reset), be more careful about clearing
        // Block if we just had a selected address and now getting empty from non-input reasons
        if (newInputValue === '' && selectedAddress !== '' && reason !== 'input' && reason !== 'clear') {
          console.log('[AddressAutocomplete] Blocking onInputChange - preventing clear of selected address from non-input reason');
          return;
        }

        // Block if the new input value is empty and we have an external value from non-input reasons
        if (newInputValue === '' && value !== '' && reason !== 'input' && reason !== 'clear') {
          console.log('[AddressAutocomplete] Blocking onInputChange - external value exists, preventing clear from non-input reason');
          return;
        }

        console.log(`[AddressAutocomplete] Calling onInputChange with: "${newInputValue}"`);
        if (onInputChange) {
          onInputChange(newInputValue);
        }
      }}
      disabled={disabled}
      loading={false}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Start typing an address..."
          fullWidth
          error={error}
          helperText={helperText}
          onBlur={() => {
            // Reset editing flag when field loses focus
            setIsUserEditing(false);
          }}
        />
      )}
      renderOption={(props, option) => {
        // eslint-disable-next-line react/prop-types
        const { key, ...otherProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
        return (
          <Box component="li" key={key} {...otherProps}>
            <Grid container alignItems="center">
              <Grid>
                <Box sx={{ mr: 2, display: 'flex' }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Box>
              </Grid>
              <Grid sx={{ flexGrow: 1 }}>
                <Box sx={{ fontWeight: 'bold' }}>
                  {option.structured_formatting?.main_text || option.description}
                </Box>
                {option.structured_formatting?.secondary_text && (
                  <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {option.structured_formatting.secondary_text}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        );
      }}
    />
  );
};

export default AddressAutocomplete;
